/**
 * How long a request has left.
 *
 * Expiry is the whole reason this queue is urgent. The shipped policies give most operations
 * forty-eight hours and anything that turns on a balance twelve — a withdrawal approved against
 * Monday's balance is worse than one that lapsed — and a lapsed request is not recoverable from
 * this screen: it disappears from the inbox and the maker has to submit the operation again.
 *
 * The countdown is computed from the served timestamp at render, never stored. The inbox re-reads
 * itself every minute, which is what keeps the figure moving without a second clock in the client.
 */

export type ExpiryUrgency = 'expired' | 'critical' | 'soon' | 'normal' | 'unknown'

export interface ExpiryState {
  urgency: ExpiryUrgency
  /** Negative once the deadline has passed. Zero when the timestamp could not be read. */
  minutesRemaining: number
  /** "3h 40m left", "Expired", "Deadline unreadable". */
  label: string
}

const MINUTE = 60 * 1000

/** Inside an hour a request needs someone now; inside six it needs someone today. */
const CRITICAL_MINUTES = 60
const SOON_MINUTES = 6 * 60

export function describeExpiry(expiresAtUtc: string, now: number = Date.now()): ExpiryState {
  const deadline = Date.parse(expiresAtUtc)

  if (Number.isNaN(deadline)) {
    // Never rendered as "expires soon" or as plenty of time. A deadline that cannot be read is a
    // fact about the data, and guessing either way misleads in one direction or the other.
    return { urgency: 'unknown', minutesRemaining: 0, label: 'Deadline unreadable' }
  }

  const millisecondsRemaining = deadline - now

  // Expiry is decided on the instant, not on the floored minute. Flooring first would call a
  // request with forty seconds left "Expired" and disable its Countersign button, when the server
  // would still have accepted it — the queue's whole urgency is in that last hour.
  if (millisecondsRemaining <= 0) {
    return {
      urgency: 'expired',
      minutesRemaining: Math.floor(millisecondsRemaining / MINUTE),
      label: 'Expired',
    }
  }

  // Rounded up for the same reason: a request with forty seconds left has a minute's worth of
  // deadline to show, and "0m left" on a row somebody may still act on reads as lapsed.
  const minutesRemaining = Math.ceil(millisecondsRemaining / MINUTE)

  const hours = Math.floor(minutesRemaining / 60)
  const minutes = minutesRemaining % 60
  const label = hours > 0 ? `${String(hours)}h ${String(minutes)}m left` : `${String(minutes)}m left`

  return {
    urgency:
      minutesRemaining <= CRITICAL_MINUTES
        ? 'critical'
        : minutesRemaining <= SOON_MINUTES
          ? 'soon'
          : 'normal',
    minutesRemaining,
    label,
  }
}

/** How long a request has been waiting, in the same shape the deadline is written in. */
export function describeWait(madeAtUtc: string, now: number = Date.now()): string {
  const made = Date.parse(madeAtUtc)
  if (Number.isNaN(made)) {
    return 'Submitted at an unreadable time'
  }

  const minutes = Math.max(0, Math.floor((now - made) / MINUTE))
  if (minutes < 60) {
    return `${String(minutes)}m ago`
  }

  const hours = Math.floor(minutes / 60)
  if (hours < 48) {
    return `${String(hours)}h ago`
  }

  return `${String(Math.floor(hours / 24))}d ago`
}
